import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { getTenant } from '../config/tenants';

// Arena's Branded Login Page
export function ArenaLogin() {
  const tenant = getTenant('arena');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const rememberedEmail = localStorage.getItem('arena_remember_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Arena client credentials
    if (email.toLowerCase() === 'vikas@esportsxo.gg' && password === '9873060792') {
      localStorage.setItem('client_authenticated', 'true');
      localStorage.setItem('client_tenant', 'arena');
      if (rememberMe) {
        localStorage.setItem('arena_remember_email', email);
      }
      navigate('/client/dashboard');
    } else {
      setError('Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ background: tenant.colors.background }}>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 p-1">
              <img src={tenant.logo} alt={tenant.name} className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-bold text-xl" style={{ color: tenant.colors.text }}>{tenant.agentName}</h1>
              <p className="text-sm" style={{ color: tenant.colors.textMuted }}>Admin Portal</p>
            </div>
          </div>

          <h2 className="font-bold text-2xl mb-2" style={{ color: tenant.colors.text }}>Welcome back</h2>
          <p className="mb-8" style={{ color: tenant.colors.textMuted }}>Sign in to manage your AI agent</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg text-sm" style={{ background: '#F5920B20', border: '1px solid #F5920B40', color: '#F59E0B' }}>
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label style={{ color: tenant.colors.text }}>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@arenaesports.ae"
                required
                className="border text-white placeholder:opacity-50"
                style={{ background: tenant.colors.surface, borderColor: tenant.colors.border }}
                data-testid="arena-login-email"
              />
            </div>

            <div className="space-y-2">
              <Label style={{ color: tenant.colors.text }}>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="border text-white placeholder:opacity-50 pr-10"
                  style={{ background: tenant.colors.surface, borderColor: tenant.colors.border }}
                  data-testid="arena-login-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: tenant.colors.textMuted }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded"
                style={{ accentColor: tenant.colors.primary }}
              />
              <label htmlFor="remember" className="text-sm" style={{ color: tenant.colors.textMuted }}>Remember me</label>
            </div>

            <Button type="submit" className="w-full font-bold" style={{ background: tenant.colors.primary, color: '#000' }} disabled={loading} data-testid="arena-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12" style={{ background: `linear-gradient(135deg, ${tenant.colors.primary}15 0%, ${tenant.colors.background} 100%)` }}>
        <div className="text-center">
          <div className="w-32 h-32 rounded-2xl overflow-hidden mx-auto mb-6 bg-white/10 p-2">
            <img src={tenant.logo} alt={tenant.name} className="w-full h-full object-contain" />
          </div>
          <h2 className="font-bold text-3xl mb-2" style={{ color: tenant.colors.text }}>{tenant.name}</h2>
          <p style={{ color: tenant.colors.textMuted }}>Powered by {tenant.agentName}</p>
        </div>
      </div>
    </div>
  );
}
